import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction, type TransactionContext } from "@telemetry/runTransaction";

export interface AzureUploadParams {
  file: File;
  blobUrl: string;
  sasToken: string;
  certificateType: string;
  storeKey: string;
  idx?: number;
}

const SERVICE = "bff-gatepass";

export const useAzureBlobUpload = ({
  handleUploadSuccess,
  handleUploadError,
  transactionContext,
}: {
  handleUploadSuccess: (
    dataArray: { documentUrl: string; certificateType: string }[],
    context: { certificateType: string; storeKey: string; idx?: number },
  ) => void;
  handleUploadError: (error: Error) => void;
  transactionContext?: TransactionContext | ((params: AzureUploadParams) => TransactionContext);
}) => {
  const qc = useQueryClient();

  return useMutation<{ documentUrl: string; certificateType: string }[], Error, AzureUploadParams>({
    mutationFn: async (params) => {
      const { file, blobUrl, sasToken, certificateType } = params;
      const upload = () => uploadFileToAzureBlob({ file, blobUrl, sasToken, certificateType });

      if (!transactionContext) {
        return upload();
      }

      const context =
        typeof transactionContext === "function" ? transactionContext(params) : transactionContext;

      return runTransaction(context, upload);
    },
    onSuccess: (dataArray, variables) => {
      handleUploadSuccess(dataArray, {
        certificateType: variables.certificateType,
        storeKey: variables.storeKey,
        idx: variables.idx,
      });
      qc.invalidateQueries({ queryKey: ["documents", SERVICE] });
    },
    onError: (error) => handleUploadError(error),
  });
};
